import { Maybe } from "@saleor/graphql";
import { getPreviousDate } from "@saleor/utils/date";
import { formatThousandDecimal } from "@saleor/utils/number";
import { isBefore } from "date-fns";
import { useIntl } from "react-intl";
import * as Yup from "yup";
import { AnyObject } from "yup/lib/types";

import { ITEM_STATUS as IS } from "../constants";
import { BATCH_FLAG_CHOICES } from "../utils";

declare module "yup" {
  interface NumberSchema<
    TType extends Maybe<number> = number | undefined,
    TContext extends AnyObject = AnyObject,
    TOut extends TType = TType
  > extends Yup.BaseSchema<TType, TContext, TOut> {
    isValidSplitQty(message): NumberSchema<TType, TContext>;
  }
  interface StringSchema<
    TType extends string = string,
    TContext extends AnyObject = AnyObject,
    TOut extends TType = TType
  > extends Yup.BaseSchema<TType, TContext, TOut> {
    isValidReqDate(message): StringSchema<TType, TContext>;
    isValidChangeOrderReqDate(message): StringSchema<TType, TContext>;
  }
}

const useNotRefValidations = (
  isChangeFlow,
  currentAction?: "none" | "add" | "split" | "edit"
) => {
  const intl = useIntl();
  const textError = intl.formatMessage({
    defaultMessage: "โปรดระบุข้อมูล"
  });

  function isValidSplitQty(message) {
    return this.test("isValidSplitQty", message, function(value) {
      const { path, createError, parent, from } = this;
      const isSplitSubmit = from?.[1]?.value?.isSplitSubmit;
      const shouldShowErrorMessage = isSplitSubmit && !!parent?.isSplit;
      if (!value || +value <= 0) {
        return createError({
          path,
          message: shouldShowErrorMessage ? "โปรดระบุจำนวน" : " "
        });
      }
      if (!parent?.isSplit) {
        if (parent?.orderType === "ZP20") {
          if (
            parent?.itemStatusEn === "Partial Delivery" &&
            (value > parent?.originalQuantity || value < parent?.deliveryQty)
          ) {
            return createError({
              path,
              message:
                value > parent?.originalQuantity
                  ? "Can't increase the quantity over" +
                    parent?.originalQuantity
                  : "Can't reduce below " + parent?.deliveryQty
            });
          }

          if (
            parent?.itemStatusEn === "Item Created" &&
            value > parent?.originalQuantity
          ) {
            return createError({
              path,
              message:
                "Can't increase the quantity over" + parent?.originalQuantity
            });
          }
        }
        if (
          parent?.itemStatusEn === "Partial Delivery" &&
          value < parent?.deliveryQty
        ) {
          return createError({
            path,
            message: "Can't reduce below " + parent?.deliveryQty
          });
        }
        if (
          parent?.itemStatusEn === "Completed Delivery" &&
          value < parent?.originalQuantity
        ) {
          return createError({
            path,
            message: "Can't reduce below " + parent?.originalQuantity
          });
        }
      }
      if (parent?.isSplit) {
        const splitItem = from?.[1]?.value?.lines?.find(
          line => line.splitOriginal && !line?.parentItemNo
        );
        // Warning: Changing the error message will affect getErrorSummary()
        if (
          parent.itemStatusEn === IS.PARTIAL_DELIVERY &&
          +splitItem?.quantity < splitItem?.deliveryQty
        ) {
          return createError({
            path,
            message: `จัดส่งแล้ว ${formatThousandDecimal(
              splitItem?.deliveryQty ?? 0,
              0
            )}`
          });
        }
        if (+splitItem?.quantity <= 0) {
          return createError({
            path,
            message: `จัดส่งแล้ว ${formatThousandDecimal(
              splitItem?.assignQuantity ?? 0,
              0
            )}`
          });
        }
      }
      return true;
    });
  }

  function isValidReqDate(message) {
    return this.test("isValidReqDate", message, function(value) {
      const { path, createError, parent, from } = this;
      const isSplitSubmit = from?.[1]?.value?.isSplitSubmit;
      const shouldShowErrorMessage = isSplitSubmit && !!parent?.isSplit;
      if (!value) {
        return createError({
          path,
          message: shouldShowErrorMessage ? "โปรดระบุวันที่" : " "
        });
      }
      if (
        value &&
        isBefore(new Date(value).setHours(0, 0, 0, 0), getPreviousDate())
      ) {
        return createError({
          path,
          message: isSplitSubmit
            ? "กรุณาเปลี่ยน “Request Date” (ไม่สามารถใส่วันที่ผ่านมาแล้วได้)"
            : " "
        });
      }
      return true;
    });
  }

  function isValidChangeOrderReqDate(message) {
    return this.test("isValidReqDate", message, function(value) {
      const { path, createError, from } = this;
      const prevReqDate = from[0]?.value?.prevReqDate;
      const parentItemNo = from[0]?.value?.parentItemNo;
      if (!value && !parentItemNo) {
        return createError({
          path,
          message: " "
        });
      }
      if (
        value !== prevReqDate &&
        !parentItemNo &&
        isBefore(new Date(value).setHours(0, 0, 0, 0), getPreviousDate())
      ) {
        return createError({
          path,
          message:
            message === "header"
              ? "กรุณาเปลี่ยน “Request Date” (ไม่สามารถใส่วันที่ผ่านมาแล้วได้)"
              : " "
        });
      }
      return true;
    });
  }

  Yup.addMethod(Yup.number, "isValidSplitQty", isValidSplitQty);
  Yup.addMethod(Yup.string, "isValidReqDate", isValidReqDate);
  Yup.addMethod(
    Yup.string,
    "isValidChangeOrderReqDate",
    isValidChangeOrderReqDate
  );

  const HeaderSchema = Yup.object().shape({
    salesOrganizationCode: Yup.string().required(" "),
    distributionChannelCode: Yup.string().required(" "),
    divisionCode: Yup.string().required(" "),
    requestDate: Yup.string()
      .transform((_, oV) => oV ?? "")
      .isValidChangeOrderReqDate("header"),

    orderType: Yup.string().required(" "),
    paymentTerm: Yup.string().required(" "),
    taxClass: Yup.string().required(" ")
  });

  const ItemDetailsSchema = Yup.object().shape({
    lines: Yup.array()
      .of(
        Yup.object().shape({
          quantity: Yup.number().isValidSplitQty(""),
          pricePerUnit: Yup.string().test(
            "nonEmptypricePerUnit",
            " ",
            (value, { parent }) => {
              if (parent?.materialType === "85" && parent?.manualPriceFlag) {
                return !!value && parseFloat(value) !== 0;
              }
              return true;
            }
          ),
          batchNo: Yup.string().test(
            "isValidBatchNo",
            " ",
            (value, { parent }) => {
              if (parent?.batchChoiceFlag === BATCH_FLAG_CHOICES.BLANK) {
                return true;
              }
              if (
                parent?.batchFlag &&
                (parent?.batchChoiceFlag === BATCH_FLAG_CHOICES.TEXT ||
                  !(parent?.bomFlag && !parent?.parentItemNo))
              ) {
                return (
                  !!value && value === value.trim() && !/[,*&!]/.test(value)
                );
              }
              return true;
            }
          ),
          ...(currentAction === "split"
            ? {
                requestDate: Yup.string()
                  .transform((_, oV) => oV ?? "")
                  .isValidReqDate(" ")
              }
            : {
                requestDate: Yup.string()
                  .transform((_, oV) => oV ?? "")
                  .isValidChangeOrderReqDate(" ")
              }),
          ...(isChangeFlow && currentAction !== "add"
            ? {
                saleQtyFactor: Yup.number()
                  .moreThan(0, " ")
                  .required(" ")
              }
            : {})
        })
      )
      .min(1)
      .required()
  });

  const lengthValidator = (value, max) => {
    const length = value?.length;
    if (!length) {
      return true; // validation passed
    } else if (length === max) {
      return true;
    } else {
      return false;
    }
  };

  const ShipToBillToSchema = Yup.object().shape({
    name1: Yup.string().required(textError),
    city: Yup.string().required(textError),
    countryCode: Yup.string().required(textError),
    taxId: Yup.string().test("len", " ", value => lengthValidator(value, 13)),
    branchId: Yup.string().test("len", " ", value => lengthValidator(value, 5))
  });

  const OneTimeToSchema = Yup.object().shape({
    soldToCode: Yup.string().required(textError),
    name1: Yup.string().required(textError),
    city: Yup.string().required(textError),
    salesEmployee: Yup.string().required(textError),
    paymentTerm: Yup.string().required(textError),
    taxClass: Yup.string().required(textError),
    taxId: Yup.string().test("len", " ", value => lengthValidator(value, 13)),
    branchId: Yup.string().test("len", " ", value => lengthValidator(value, 5))
  });
  return {
    HeaderSchema,
    ItemDetailsSchema,
    ShipToBillToSchema,
    OneTimeToSchema
  };
};

export default useNotRefValidations;
